<script lang="ts" setup>
const route = useRoute();
const userStore = useUserStore();
const {
  getPKfromRescueKey,
  clearRescueRecipientKey,
  confirmPKexists,
} = useWeb3();
const { createAuthAttemptAndRedirect } = useAuthController();
const { createOTPAuthAttemptAndRedirect } = useRescueController();
const { updateWalletKeys } = useUserController();
const showModalLoader = ref(true);
const showModalEnd = ref(false);
const showKeysSocialAccount = ref(false);
const showKeysRecoveryPhrase = ref(false);
const success = ref(false);
const localePath = useLocalePath();

const modalRescueText = computed(() => {
  if (route.query.status || route.query.rescue || userStore.isLoggedIn) {
    return {
      title: "main.modalRescue.title",
      bodyTitle: "main.modalRescue.recoveringAccount",
      bodySubtitle: "main.modalRescue.recoveringAccountDescription",
    };
  } else {
    return {
      title: "main.modalRescue.title",
      bodyTitle: "main.modalRescue.redirectingToGFALID",
      bodySubtitle: "main.modalRescue.establishingSecureConnection",
    };
  }
});

const beginUsersVaultReset = async () => {
  try {
    useUserStore().walletLocked = false;
    useModalManager().closeModal("modal-user-wallet-locked");
    const rescueKey = userStore.keys?.rescue?.value;
    if (!rescueKey) throw new Error("Missing rescue key");
    await getPKfromRescueKey(userStore.id, rescueKey);
    await confirmPKexists();
    showModalLoader.value = false;
    backToUsingSocial();
  } catch (error) {
    console.error("Failed to begin users vault reset", error);
    onError();
  }
};

const skipUsingSocial = () => {
  showKeysSocialAccount.value = false;
  showKeysRecoveryPhrase.value = true;
};

const backToUsingSocial = () => {
  showKeysRecoveryPhrase.value = false;
  showKeysSocialAccount.value = true;
};

const getPassword = async () => {
  const rescue = useCookie<RescueCookie>(`gfalIdRescue-${userStore.id}`);
  if (!rescue.value) throw new Error("Missing rescue");
  const stringifiedPayload = await deobfuscate(rescue.value.secret);
  const { type, value } = JSON.parse(stringifiedPayload) as {
    type: keyof typeof userStore.keys;
    value: string;
  };
  if (type !== "password") return null;
  return value;
};

const submitEncryptedKey = async ({
  method,
  value,
}: {
  method: UserKeyMethod;
  value: string;
}) => {
  try {
    showModalLoader.value = true;
    showKeysRecoveryPhrase.value = false;
    showKeysSocialAccount.value = false;
    const password = await getPassword();
    const response = await updateWalletKeys({
      method,
      value,
      password,
    });
    if (response.statusCode !== 200)
      throw new Error("Failed to update wallet keys");
    success.value = true;
    showModalLoader.value = false;
    showModalEnd.value = true;
  } catch (error) {
    console.error("Failed to update wallet keys", error);
    onError();
  }
};

const encryptKeyWithLinkedSocial = async () => {
  try {
    const rescue = useCookie<RescueCookie>(`gfalIdRescue-${userStore.id}`);
    if (!rescue.value) throw new Error("Missing rescue");
    if (!rescue.value.link) throw new Error("Missing rescue link");
    const linkedParsedSecret = await parseSecret(rescue.value.link);
    if (!linkedParsedSecret) throw new Error("Failed to parse rescue link");
    await submitEncryptedKey({
      method: linkedParsedSecret.type,
      value: linkedParsedSecret.value,
    });
  } catch (error) {
    console.error("Failed to encrypt key with linked social", error);
    onError();
  }
};

const modalWidth = computed(() => {
  if (showModalLoader.value || showModalEnd.value) return "medium";
  if (showKeysSocialAccount.value || showKeysRecoveryPhrase.value) return "xl";
  return "xl";
});

const checkForPasswordSecretinCookie = async () => {
  if (!userStore.isLoggedIn) {
    createAuthAttemptAndRedirect("password-forgot", getCurrentFullPath(), true);
    return;
  }

  const otpCookie = useCookie(`otp-${userStore.id}`);
  if (otpCookie.value) return;

  const gfalIdRescue = useCookie<RescueCookie>(`gfalIdRescue-${userStore.id}`);

  if (gfalIdRescue.value && gfalIdRescue.value.secret) {
    await createOTPAuthAttemptAndRedirect().catch(onError);
    return;
  }

  const gfalid = useCookie(`gfalid-${useUserStore().id}`).value;
  if (gfalid) {
    gfalIdRescue.value = {
      secret: gfalid,
      link: null,
      web3AuthKey: null,
    };
    await createOTPAuthAttemptAndRedirect().catch(onError);
    return;
  }

  await confirmPKexists()
    .then((exists) => {
      if (exists) navigateTo(localePath("/"));
    })
    .catch(() => {
      createAuthAttemptAndRedirect(
        "password-forgot",
        getCurrentFullPath(),
        true
      );
    });
};

const onError = () => {
  clearRescueRecipientKey();
  showModalLoader.value = false;
  showModalEnd.value = true;
  success.value = false;
};

onMounted(async () => {
  if (route.query.status === "error") {
    onError();
    return;
  }

  if (route.query.status === "connected") {
    await beginUsersVaultReset();
    return;
  } else if (route.query.rescue) {
    encryptKeyWithLinkedSocial();
    return;
  } else {
    checkForPasswordSecretinCookie();
  }
});
</script>

<template>
  <BaseLayoutModal :width="modalWidth" :hide-x="true">
    <template #header>
      {{ $t("main.modalRescue.title") }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalLoader
          v-if="showModalLoader"
          :title="$t(modalRescueText.title)"
          :body-title="$t(modalRescueText.bodyTitle)"
          :body-subtitle="$t(modalRescueText.bodySubtitle)" />
        <ModalKeysSocialAccount
          v-else-if="showKeysSocialAccount"
          rescue
          @skip="skipUsingSocial" />
        <ModalKeysRecoveryPhrase
          v-else-if="showKeysRecoveryPhrase"
          @continue="submitEncryptedKey"
          @back="backToUsingSocial" />
        <ModalRescueEnd v-else-if="showModalEnd" :success="success" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>
